import React, {useState, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {useQueryClient} from '@tanstack/react-query';
import {Offer, DEAL_STATUSES, OFFER_CONFIRMATION_TYPES,QUERY_CACHE_KEYS} from '@chekin/core';
import {ShoppingCart} from 'lucide-react';
import {Button, PriceBadge} from '@guestapp/old-ui-kit';
import {usePaymentSettings, useGuestGroup, useCreateBulkDealMutation} from 'hooks';
import {useReservation} from 'context/reservation';
import {formatPrice, getPrice} from 'components/upselling/price';
import {Counter} from 'components/common/Counter';
import {CounterSize} from 'components/common/Counter/styled';

export type DiscountInfo = {
  percentage: number;
  originalPrice: string;
};
import {
  DealCardContainer,
  CardImageContainer,
  CardImage,
  CardGradientOverlay,
  SwipeIndicator,
  CardContent,
  CardTitle,
  CardHighlight,
  CardActions,
  PriceBadgeContainer,
  QuantityContainer,
  QuantityLabel,
  QuantityTitle,
  QuantityPrice,
  TotalPriceRow,
  TotalLabel,
  TotalAmount,
} from './styled';

const SWIPE_THRESHOLD = 100;

type DealCardWithCartProps = {
  offer: Offer;
  stackIndex: number;
  isDragging: boolean;
  translateX: number;
  rotation: number;
  isExiting: boolean;
  exitDirection: 'left' | 'right' | null;
  discount?: DiscountInfo;
  onSkip: () => void;
  onAddedToCart: () => void;
  onDragStart: (e: React.MouseEvent | React.TouchEvent) => void;
  onDragMove: (e: React.MouseEvent | React.TouchEvent) => void;
  onDragEnd: () => void;
};

export const DealCardWithCart = React.memo(function DealCardWithCart({
  offer,
  stackIndex,
  isDragging,
  translateX,
  rotation,
  isExiting,
  exitDirection,
  discount,
  onSkip,
  onAddedToCart,
  onDragStart,
  onDragMove,
  onDragEnd,
}: DealCardWithCartProps) {
  const {t} = useTranslation();
  const queryClient = useQueryClient();
  const {currencySymbol} = usePaymentSettings();
  const {data: reservation} = useReservation();
  const {guestGroup} = useGuestGroup();

  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);

  const {mutate: createDeal, isPending} = useCreateBulkDealMutation({
    onSuccess: () => {
      setIsAdding(false);
      void queryClient.invalidateQueries({queryKey: [QUERY_CACHE_KEYS.paymentsCart]});
      onAddedToCart();
    },
    onError: () => {
      setIsAdding(false);
    },
  });

  const skipOpacity = Math.min(1, Math.max(0, -translateX / 100));
  const addOpacity = Math.min(1, Math.max(0, translateX / 100));


  const unitPrice = getPrice(offer);
  const totalPrice = unitPrice * quantity;

  let displayPrice = formatPrice(currencySymbol)({
    priceType: offer.price_type,
    items: offer.items,
  });

  if (!displayPrice && offer.items?.[0]?.price) {
    displayPrice = `${currencySymbol}${offer.items[0].price}`;
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (stackIndex !== 0) return;
    e.preventDefault();
    onDragStart(e);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (stackIndex !== 0) return;
    onDragStart(e);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (stackIndex !== 0 || !isDragging) return;
    onDragMove(e);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (stackIndex !== 0) return;
    onDragMove(e);
  };

  const handleDragEnd = () => {
    if (stackIndex !== 0) return;

    if (translateX >= SWIPE_THRESHOLD) {
      triggerAddToCart();
    }
    onDragEnd();
  };

  const handleSkipClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSkip();
    },
    [onSkip],
  );

  const handleQuantityChange = useCallback((value: number) => {
    setQuantity(value);
  }, []);

  const triggerAddToCart = useCallback(() => {
    if (!reservation || !offer || isAdding || isPending) {
      return;
    }

    const coreGuest = guestGroup?.members.find(({id}) => id === guestGroup?.leader_id);
    const requestedItemId = offer.items?.[0]?.id;

    if (!requestedItemId) {
      return;
    }

    setIsAdding(true);

    const guestEmail = coreGuest?.email || reservation.default_invite_email || undefined;

    createDeal({
      deals: [
        {
          core_housing_id: reservation.housing_id,
          core_reservation_id: reservation.id,
          core_guest_name: coreGuest?.full_name,
          core_housing_name: reservation.housing_name,
          core_room_numbers: reservation.rooms?.map(room => room.number) || [],
          guest_email: guestEmail,
          core_guest_id: coreGuest?.id,
          offer_id: offer.id,
          quantity: 1,
          payments_version: 'V2' as const,
          number_of_people: quantity,
          requested_items: [
            {
              item: requestedItemId,
              quantity,
            },
          ],
          status:
            offer.confirmation_type === OFFER_CONFIRMATION_TYPES.auto
              ? DEAL_STATUSES.approved
              : DEAL_STATUSES.pre_requested,
        },
      ],
    });
  }, [reservation, offer, guestGroup, quantity, createDeal, isAdding, isPending]);

  const handleAddToCart = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      triggerAddToCart();
    },
    [triggerAddToCart],
  );

  return (
    <DealCardContainer
      $isDragging={isDragging}
      $translateX={translateX}
      $rotation={rotation}
      $stackIndex={stackIndex}
      $isExiting={isExiting}
      $exitDirection={exitDirection}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleDragEnd}
    >
      {stackIndex === 0 && (
        <>
          <SwipeIndicator $direction="left" $opacity={skipOpacity}>
            {t('skip')}
          </SwipeIndicator>
          <SwipeIndicator $direction="right" $opacity={addOpacity}>
            {t('enhance_your_stay.add_to_cart')}
          </SwipeIndicator>
        </>
      )}

      <CardImageContainer>
        <CardImage src={offer.image_url} alt={offer.title} />
        <CardGradientOverlay />
        <PriceBadgeContainer>
          <PriceBadge
            price={displayPrice || t('enhance_your_stay.view_price')}
            originalPrice={discount?.originalPrice}
            discountPercentage={discount?.percentage}
          />
        </PriceBadgeContainer>
      </CardImageContainer>

      <CardContent>
        <CardTitle>{offer.title}</CardTitle>
        <CardHighlight>{offer.highlight}</CardHighlight>

        <QuantityContainer onClick={e => e.stopPropagation()}>
          <QuantityLabel>
            <QuantityTitle>{t('enhance_your_stay.quantity')}</QuantityTitle>
            <QuantityPrice>
              {currencySymbol}
              {unitPrice.toFixed(2)} {t('enhance_your_stay.per_unit')}
            </QuantityPrice>
          </QuantityLabel>
          <Counter
            value={quantity}
            onChange={handleQuantityChange}
            minNumber={1}
            maxNumber={99}
            size={CounterSize.S}
          />
        </QuantityContainer>

        <TotalPriceRow>
          <TotalLabel>{t('enhance_your_stay.total')}</TotalLabel>
          <TotalAmount>
            {currencySymbol}
            {totalPrice.toFixed(2)}
          </TotalAmount>
        </TotalPriceRow>

        <CardActions>
          <Button
            danger
            short
            label={t('skip')}
            onClick={handleSkipClick}
            disabled={stackIndex !== 0 || isAdding || isPending}
          />
          <Button
            short
            label={
              isAdding || isPending
                ? t('enhance_your_stay.adding')
                : t('enhance_your_stay.add_to_cart')
            }
            onClick={handleAddToCart}
            disabled={stackIndex !== 0 || isAdding || isPending}
            customIcon={<ShoppingCart size={16} />}
            iconProps={{position: 'left'}}
          />
        </CardActions>
      </CardContent>
    </DealCardContainer>
  );
});
